import { Button } from '@/components/ui/button';
import GlassCard from '@/components/ui/glass-card';
import { Input } from '@/components/ui/input';
import { useSelectedLocation } from '@/hooks/use-selected-location';
import { apiService } from '@/lib/api-service';
import { clearSelectedAppointment } from '@/store/slices/dataSlice';
import {
  Calendar,
  Clock,
  Loader2,
  Search,
  SearchIcon,
  UserRound,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

export interface ScribeJob {
  id: string;
  location_id: string;
  patient_id: string;
  appointment_id: string;
  pt_first_name: string;
  pt_last_name: string;
  appointment_start_time: string;
  pt_dob: string;
  pt_gender: string;
  pt_email: string;
  pt_phone_number: string;
  service: string;
  audio_file_key?: string;
  transcript_file_key?: string;
  audio_file_url?: string;
  transcript_file_url?: string;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  eyecare_report_key?: string;
  start_time: string;
  created_by: string;
  end_time: string;
}

interface EncounterHistoryProps {
  onEncounterSelect?: (encounter: ScribeJob) => void;
  refreshTrigger?: number; // Used to trigger refresh of encounters list
  selectedEncounter?: ScribeJob;
}

const EncounterHistory = ({
  onEncounterSelect,
  refreshTrigger,
  selectedEncounter,
}: EncounterHistoryProps) => {
  const dispatch = useDispatch();
  const [searchFields, setSearchFields] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
  });
  const [encounters, setEncounters] = useState<ScribeJob[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const [currentPage, setCurrentPage] = useState(0);
  const [allLoaded, setAllLoaded] = useState(false);
  const { selectedLocation } = useSelectedLocation();

  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [initialEncountersLoaded, setInitialEncountersLoaded] = useState(false);

  const [currentEncounter, setCurrentEncounter] = useState<ScribeJob | null>(
    selectedEncounter || null
  );

  const ITEMS_PER_PAGE = 20;
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const stateRef = useRef({ loadingMore, allLoaded, hasSearched, currentPage });
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    if (selectedEncounter) {
      setCurrentEncounter(selectedEncounter);
    }
  }, [selectedEncounter]);

  // Load initial encounters
  useEffect(() => {
    const loadInitialEncounters = async () => {
      if (selectedLocation?.locationId && !initialEncountersLoaded) {
        setLoading(true);
        await loadMoreEncounters(0, true, true);
        setInitialEncountersLoaded(true);
        setLoading(false);
      }
    };
    loadInitialEncounters();
  }, [selectedLocation?.locationId, initialEncountersLoaded]);

  // Refresh encounters when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger && selectedLocation?.locationId) {
      setEncounters([]);
      setCurrentPage(0);
      setAllLoaded(false);
      setHasSearched(false);
      loadMoreEncounters(0, true, true);
    }
  }, [refreshTrigger, selectedLocation?.locationId]);

  // Update ref whenever state changes
  useEffect(() => {
    stateRef.current = { loadingMore, allLoaded, hasSearched, currentPage };
  }, [loadingMore, allLoaded, hasSearched, currentPage]);

  const formatTime = (dateTimeString: string) => {
    const date = new Date(dateTimeString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });
  };

  const formatDateForDatabase = (dateString: string) => {
    // Convert MM/dd/yyyy to yyyy-MM-dd format for database query
    if (!dateString) return '';
    const parts = dateString.split('/');
    if (parts.length === 3) {
      const [month, day, year] = parts;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    return dateString;
  };

  const handleEncounterSelect = (encounter: ScribeJob) => {
    setCurrentEncounter(encounter);
    onEncounterSelect?.(encounter);
  };

  const buildSearchFilters = (forceLoad: boolean = false) => {
    const { firstName, lastName, dateOfBirth } = searchFields;

    // Build the filters object for exact matches
    const filters: Record<string, unknown> = {
      location_id: selectedLocation?.locationId,
    };

    // Build the likeFilters object for partial matches
    const likeFilters: Record<string, unknown> = {};

    // Use partial matching for names
    if (
      !forceLoad ||
      firstName.trim() ||
      lastName.trim() ||
      dateOfBirth.trim()
    ) {
      if (firstName.trim()) {
        likeFilters.pt_first_name = firstName.trim();
      }
      if (lastName.trim()) {
        likeFilters.pt_last_name = lastName.trim();
      }

      // Use exact matching for date of birth
      if (dateOfBirth.trim()) {
        filters.pt_dob = formatDateForDatabase(dateOfBirth.trim());
      }
    }

    return { filters, likeFilters };
  };

  const loadMoreEncounters = async (
    page: number = 0,
    reset: boolean = false,
    forceLoad: boolean = false
  ) => {
    if (!selectedLocation?.locationId) {
      console.error('No location selected');
      return;
    }

    const { firstName, lastName, dateOfBirth } = searchFields;

    // At least one search field must be filled
    if (
      !forceLoad &&
      !firstName.trim() &&
      !lastName.trim() &&
      !dateOfBirth.trim()
    ) {
      return;
    }

    if (reset) {
      setLoading(true);
      setCurrentPage(0);
      setAllLoaded(false);
    } else {
      setLoadingMore(true);
    }

    try {
      const { filters, likeFilters } = buildSearchFilters(forceLoad);
      const offset = page * ITEMS_PER_PAGE;

      // Query the ev_scribe_jobs table
      const { data, error } = await apiService.query<ScribeJob>(
        'ev_scribe_jobs',
        {
          select: '*',
          filters,
          likeFilters,
          orderBy: { column: 'start_time', ascending: false },
          limit: ITEMS_PER_PAGE,
          offset,
        }
      );

      if (error) {
        console.error('Error searching past encounters:', error);
        if (reset) {
          setEncounters([]);
        }
      } else {
        const newEncounters = data || [];

        if (reset) {
          setEncounters(newEncounters);
        } else {
          setEncounters((prev) => [...prev, ...newEncounters]);
        }

        // Check if we've loaded all available data
        if (newEncounters.length < ITEMS_PER_PAGE) {
          setAllLoaded(true);
        }

        if (!reset) {
          setCurrentPage(page);
        }
      }
    } catch (error) {
      console.error('Error searching past encounters:', error);
      if (reset) {
        setEncounters([]);
      }
    } finally {
      if (reset) {
        setLoading(false);
      } else {
        setLoadingMore(false);
      }
    }
  };

  const handleSearch = async () => {
    const { firstName, lastName, dateOfBirth } = searchFields;

    // At least one search field must be filled
    if (!firstName.trim() && !lastName.trim() && !dateOfBirth.trim()) {
      return;
    }

    setIsInitialLoad(false);
    setHasSearched(true);
    dispatch(clearSelectedAppointment());
    await loadMoreEncounters(0, true, false);
  };

  const handleLoadMore = useCallback(async () => {
    if (!loadingMore && !allLoaded) {
      await loadMoreEncounters(currentPage + 1, false, true);
    }
  }, [loadingMore, allLoaded, currentPage]);

  // Stable callback for intersection observer
  const handleIntersection = useCallback(async () => {
    const currentState = stateRef.current;

    // Allow loading more if we have encounters (either from search or initial load)
    const canLoadMore =
      !currentState.loadingMore &&
      !currentState.allLoaded &&
      (currentState.hasSearched || initialEncountersLoaded);

    if (canLoadMore) {
      try {
        // For initial encounters, use forceLoad=true to bypass search validation
        const forceLoad = !currentState.hasSearched && initialEncountersLoaded;
        await loadMoreEncounters(
          currentState.currentPage + 1,
          false,
          forceLoad
        );
      } catch (error) {
        console.error('❌ Error loading more encounters:', error);
      }
    }
  }, [initialEncountersLoaded, searchFields]);

  // Callback ref for trigger element - sets up observer when element is mounted
  const loadMoreTriggerRef = useCallback(
    (element: HTMLDivElement | null) => {
      // Clean up existing observer
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }

      // Only set up observer if element exists and conditions are met
      if (
        element &&
        (hasSearched || (initialEncountersLoaded && encounters.length > 0)) &&
        !allLoaded &&
        encounters.length > 0
      ) {
        const scrollContainer = scrollContainerRef.current;

        const observer = new IntersectionObserver(
          (entries) => {
            const entry = entries[0];
            if (entry?.isIntersecting) {
              handleIntersection();
            }
          },
          {
            threshold: 0.1,
            rootMargin: '100px',
            root: scrollContainer,
          }
        );

        observer.observe(element);
        observerRef.current = observer;
      }
    },
    [
      hasSearched,
      allLoaded,
      encounters.length,
      initialEncountersLoaded,
      handleIntersection,
    ]
  );

  // Cleanup observer on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const handleFieldChange = (field: string, value: string) => {
    setSearchFields((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Reset pagination state when search fields change
    if (hasSearched) {
      setCurrentPage(0);
      setAllLoaded(false);
      setEncounters([]);
      setHasSearched(false);
      setIsInitialLoad(false);
    }
  };

  const canSearch =
    searchFields.firstName.trim() ||
    searchFields.lastName.trim() ||
    searchFields.dateOfBirth.trim();

  return (
    <div className="flex flex-col h-full">
      {/* Search Section */}
      <div className="flex-shrink-0 pb-2">
        {/* Search Fields */}
        <div className="space-y-1 px-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div className="relative group flex flex-col gap-1">
              <Input
                type="text"
                className="bg-white/60 rounded-3xl dark:bg-slate-700/60 backdrop-blur-md border border-gray-400/40 dark:border-slate-600/40 shadow-sm focus-visible:ring-2 focus-visible:ring-eyevantage-primary/30 focus-visible:border-eyevantage-primary/50 placeholder:text-slate-500/80 dark:placeholder:text-slate-400/80 text-md font-medium transition-all duration-300"
                placeholder="Last Name"
                value={searchFields.lastName}
                onChange={(e) => handleFieldChange('lastName', e.target.value)}
                aria-label="Last Name"
                autoComplete="patient-last-name"
              />
            </div>
            <div className="relative group flex flex-col gap-1">
              <Input
                type="text"
                placeholder="First Name"
                className="rounded-3xl bg-white/60 dark:bg-slate-700/60 backdrop-blur-md border border-gray-400/40 dark:border-slate-600/40 shadow-sm focus-visible:ring-2 focus-visible:ring-eyevantage-primary/30 focus-visible:border-eyevantage-primary/50 placeholder:text-slate-500/80 dark:placeholder:text-slate-400/80 text-md font-medium transition-all duration-300"
                value={searchFields.firstName}
                onChange={(e) => handleFieldChange('firstName', e.target.value)}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 items-end">
            <div className="relative group flex flex-col gap-0">
              <label
                htmlFor="dob-input"
                className="text-md text-slate-700 dark:text-slate-300 mb-0 whitespace-nowrap ml-2">
                Date of Birth
              </label>
              <Input
                id="dob-input"
                type="date"
                placeholder="Date of Birth"
                className="rounded-3xl bg-white/60 dark:bg-slate-700/60 backdrop-blur-md border border-gray-400/40 dark:border-slate-600/40 shadow-sm focus-visible:ring-2 focus-visible:ring-eyevantage-primary/30 focus-visible:border-eyevantage-primary/50 placeholder:text-slate-500/80 dark:placeholder:text-slate-400/80 text-md font-medium transition-all duration-300 pr-2 w-auto"
                value={
                  searchFields.dateOfBirth
                    ? (() => {
                        // Convert MM/DD/YYYY to YYYY-MM-DD for input type="date"
                        const [mm, dd, yyyy] =
                          searchFields.dateOfBirth.split('/');
                        if (mm && dd && yyyy) {
                          return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(
                            2,
                            '0'
                          )}`;
                        }
                        return '';
                      })()
                    : ''
                }
                onChange={(e) => {
                  // Convert YYYY-MM-DD to MM/DD/YYYY for internal state
                  const val = e.target.value;
                  if (val) {
                    const [yyyy, mm, dd] = val.split('-');
                    handleFieldChange('dateOfBirth', `${mm}/${dd}/${yyyy}`);
                  } else {
                    handleFieldChange('dateOfBirth', '');
                  }
                }}
                aria-label="Date of Birth"
                autoComplete="bday"
              />
            </div>
            <div className="relative group flex flex-col gap-1">
              <Button
                onClick={handleSearch}
                disabled={loading || !canSearch}
                className="rounded-3xl bg-gradient-to-r from-purple-500 to-blue-800 hover:from-purple-500 hover:to-blue-800 text-white text-md font-medium transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed w-full">
                {loading && !isInitialLoad && !initialEncountersLoaded ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Searching...
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Search className="h-4 w-4" />
                    Search
                  </div>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Results Section */}
      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto px-2 py-2 space-y-3"
        style={{
          maxHeight: 'calc(100vh - 22rem)',
          scrollbarWidth: 'none',
          minHeight: 'calc(100vh - 22rem)',
        }}>
        {!hasSearched && !isInitialLoad && !initialEncountersLoaded ? (
          <div
            className="flex flex-col items-center justify-center text-slate-500 dark:text-slate-400 text-center"
            style={{ height: 'calc(100vh - 23rem)' }}>
            <SearchIcon className="h-20 w-20 opacity-50 mb-4" />
            <h4 className="text-xl font-light mb-3 text-slate-500 dark:text-slate-300">
              Search Past Encounters
            </h4>
            <p className="text-md opacity-70">
              Enter patient details to find previous scribe encounters
            </p>
          </div>
        ) : loading ? (
          <div
            className="flex flex-col items-center justify-center text-slate-500 dark:text-slate-400"
            style={{ height: 'calc(100vh - 23rem)' }}>
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-eyevantage-primary border-t-transparent mb-3"></div>
            <span className="text-xl font-light mb-3 text-slate-500 dark:text-slate-300">
              {hasSearched
                ? 'Searching encounters...'
                : 'Loading recent encounters...'}
            </span>
          </div>
        ) : encounters.length === 0 ? (
          <div
            className="flex flex-col items-center justify-center text-slate-500 dark:text-slate-400 text-center"
            style={{ height: 'calc(100vh - 23rem)' }}>
            <Calendar className="h-20 w-20 opacity-50 mb-4" />
            <h4 className="text-xl font-light mb-3 text-slate-500 dark:text-slate-300">
              No encounters found
            </h4>
            <p className="text-md opacity-70">
              Try adjusting your search criteria
            </p>
          </div>
        ) : (
          <>
            {encounters.map((encounter) => {
              const isSelected = currentEncounter?.id === encounter.id;
              const displayName = `${encounter.pt_first_name} ${encounter.pt_last_name}`;

              return (
                <GlassCard
                  key={encounter.id}
                  variant="default"
                  className={`cursor-pointer transition-all border border-gray-400/20 dark:border-slate-700/50 duration-300 relative ${
                    isSelected
                      ? 'ring-2 ring-purple-500/80 dark:ring-purple-500/80 shadow-lg shadow-purple-500/25 dark:shadow-purple-500/20'
                      : 'hover:shadow-xl'
                  }`}
                  onClick={() => handleEncounterSelect(encounter)}>
                  {/* Elegant selection indicators */}
                  {isSelected && (
                    <>
                      {/* Subtle glow overlay for selection */}
                      <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-blue-500/10 dark:from-purple-500/10 dark:to-blue-500/10 rounded-xl pointer-events-none" />
                    </>
                  )}

                  {/* Content container - no shifting */}
                  <div className="relative z-10">
                    {/* Header - Patient and Time */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {/* Avatar */}
                        <div className="w-8 h-8 rounded-3xl flex items-center justify-center text-md text-white bg-gradient-to-t from-slate-600 to-slate-800 dark:from-slate-700 dark:via-slate-600 dark:to-slate-600 border border-slate-300/50 dark:border-slate-500/50 transition-all duration-300">
                          {encounter.pt_first_name.charAt(0).toUpperCase()}
                          {encounter.pt_last_name.charAt(0).toUpperCase()}
                        </div>

                        <div>
                          <h3 className="text-lg text-slate-800 dark:text-slate-200 transition-colors duration-300">
                            {displayName}
                          </h3>
                        </div>
                      </div>

                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4 text-slate-500 dark:text-slate-400 transition-colors duration-300" />
                        <span className="text-sm font-medium text-slate-500 dark:text-slate-400 transition-colors duration-300">
                          {formatTime(encounter.appointment_start_time)}
                        </span>
                      </div>
                    </div>

                    {/* Service and Status */}
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-md font-medium text-slate-700 dark:text-slate-400 transition-colors duration-300">
                        {encounter.service}
                      </span>
                    </div>

                    <div className="flex items-center justify-between mb-1">
                      {/* Patient Details */}
                      <div className="flex items-center gap-2">
                        <UserRound className="h-3.5 w-3.5 text-slate-500 dark:text-slate-400 transition-colors duration-300" />
                        <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 transition-colors duration-300">
                          {encounter.pt_dob && (
                            <span>DOB: {formatDate(encounter.pt_dob)}</span>
                          )}
                          {encounter.pt_dob && encounter.pt_gender && (
                            <span className="w-1 h-1 bg-current rounded-full opacity-50" />
                          )}
                          {encounter.pt_gender && (
                            <span>{encounter.pt_gender}</span>
                          )}
                        </div>
                      </div>
                      <div
                        className={`text-sm border border-slate-300/50 dark:border-slate-500/50 rounded-md px-2 py-1 transition-all duration-300 ${
                          encounter.status.toLowerCase() === 'completed'
                            ? 'text-emerald-700 border-emerald-800'
                            : encounter.status.toLowerCase() === 'failed'
                            ? 'text-red-700 dark:text-red-300 border-red-700'
                            : encounter.status.toLowerCase() === 'processing'
                            ? 'text-orange-700 dark:text-orange-300 border-orange-700'
                            : 'text-blue-700 dark:text-blue-300 border-blue-700'
                        }`}>
                        {encounter.status.toLowerCase()}
                      </div>
                    </div>

                    {/* Date Created */}
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-500 dark:text-slate-400">
                        Encounter Dt: {formatDate(encounter.start_time)}{' '}
                        {formatTime(encounter.start_time)}
                      </span>
                    </div>
                  </div>
                </GlassCard>
              );
            })}

            {/* Load More Trigger (for intersection observer) */}
            {encounters.length > 0 && !allLoaded && (
              <div
                ref={loadMoreTriggerRef}
                className="py-4 min-h-[40px] w-full"
                style={{ minHeight: '40px' }}>
                {loadingMore && (
                  <div className="flex flex-col items-center justify-center py-4">
                    <Loader2 className="h-6 w-6 animate-spin text-purple-500 mb-2" />
                    <span className="text-sm text-slate-500 dark:text-slate-400">
                      Loading more encounters...
                    </span>
                  </div>
                )}
                {/* Trigger area for intersection detection */}
                {!loadingMore && (
                  <div className="h-8 w-full flex items-center justify-center">
                    <div
                      className="h-px w-20 bg-purple-200 dark:bg-purple-800 opacity-30"
                      aria-hidden="true"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Load More Button (fallback) */}
            {encounters.length > 0 && !allLoaded && !loadingMore && (
              <div className="flex justify-center py-4">
                <Button
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  variant="outline"
                  className="bg-white/60 dark:bg-slate-700/60 backdrop-blur-md border border-gray-400/40 dark:border-slate-600/40 hover:bg-purple-50 dark:hover:bg-slate-600/60 transition-all duration-300">
                  Load More Encounters
                </Button>
              </div>
            )}

            {/* All Results Loaded Message */}
            {encounters.length > 0 && allLoaded && (
              <div className="flex flex-col items-center justify-center py-6 text-center">
                <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mb-3">
                  <Calendar className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
                </div>
                <h4 className="text-lg font-medium text-slate-700 dark:text-slate-300 mb-1">
                  All encounters loaded
                </h4>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Showing {encounters.length} result
                  {encounters.length !== 1 ? 's' : ''}
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default EncounterHistory;
